<div
  class="filter-panel"
  [class.responsive]="isMobile"
  [class]="config.panelClass || ''"
  (keydown)="onKeydown($event)"
>
  <div class="filter-header" [class]="config.headerClass || ''">
    <h2>{{ t.FILTERS }}</h2>
    <button
      type="button"
      class="close-btn"
      (click)="closePanel.emit()"
      [attr.aria-label]="t.CLOSE_FILTERS"
    >
      <i class="bi bi-x-lg"></i>
    </button>
  </div>

  <div class="main-content">
    <div class="field-group">
      <label>{{ t.STATUS }}</label>
      <div class="status-select-container">
        <div class="status-select-trigger" (click)="toggleStatusDropdown(); $event.stopPropagation()">
          <span *ngIf="selectedStatuses.length === 0" class="placeholder">{{ t.FILTER_BY_STATUS }}</span>
          <span *ngIf="selectedStatuses.length > 0" class="selected-count">{{ selectedCountLabel(selectedStatuses.length) }}</span>
          <i class="bi bi-chevron-down"></i>
        </div>
        <div class="status-dropdown" [class.show]="isStatusDropdownOpen">
          <div class="dropdown-header">
            <span>{{ t.SELECT_LABEL }} {{ t.STATUS }}</span>
            <button type="button" class="clear-btn" (click)="clearStatuses(); $event.stopPropagation()">{{ t.CLEAR_ALL }}</button>
          </div>
          <div class="status-options">
            @for (status of sections[0].options; track status) {
              <label class="status-option">
                <div class="custom-checkbox">
                  <input
                    type="checkbox"
                    [checked]="selectedStatuses.includes(status)"
                    (change)="toggleStatus(status); $event.stopPropagation()"
                  />
                  <span class="checkmark"></span>
                </div>
                <span>{{ getStatusLabel(status) }}</span>
              </label>
            }
          </div>
        </div>
      </div>
    </div>

    <div class="field-group">
      <label>{{ t.COMPANY }}</label>
      <div class="company-select-container">
        <div class="status-select-trigger" (click)="toggleCompanyDropdown(); $event.stopPropagation()">
          <span *ngIf="selectedCompanies.length === 0" class="placeholder">{{ t.FILTER_BY_COMPANY }}</span>
          <span *ngIf="selectedCompanies.length > 0" class="selected-count">{{ selectedCountLabel(selectedCompanies.length) }}</span>
          <i class="bi bi-chevron-down"></i>
        </div>
        <div class="status-dropdown" [class.show]="isCompanyDropdownOpen">
          <div class="dropdown-header">
            <span>{{ t.SELECT_LABEL }} {{ t.COMPANY }}</span>
            <button type="button" class="clear-btn" (click)="clearCompanies(); $event.stopPropagation()">{{ t.CLEAR_ALL }}</button>
          </div>
          <div class="status-options">
            @for (company of companyOptions; track company.value) {
              <label class="status-option">
                <div class="custom-checkbox">
                  <input
                    type="checkbox"
                    [checked]="selectedCompanies.includes(company.value)"
                    (change)="toggleCompany(company.value); $event.stopPropagation()"
                  />
                  <span class="checkmark"></span>
                </div>
                <span>{{ company.label }}</span>
              </label>
            }
          </div>
        </div>
      </div>
    </div>

    <div class="field-group">
      <label>{{ t.LANGUAGE }}</label>
      <div class="language-select-container">
        <div class="status-select-trigger" (click)="toggleLanguageDropdown(); $event.stopPropagation()">
          <span *ngIf="selectedLanguages.length === 0" class="placeholder">{{ t.FILTER_BY_LANGUAGE }}</span>
          <span *ngIf="selectedLanguages.length > 0" class="selected-count">{{ selectedCountLabel(selectedLanguages.length) }}</span>
          <i class="bi bi-chevron-down"></i>
        </div>
        <div class="status-dropdown" [class.show]="isLanguageDropdownOpen">
          <div class="dropdown-header">
            <span>{{ t.SELECT_LABEL }} {{ t.LANGUAGE }}</span>
            <button type="button" class="clear-btn" (click)="clearLanguages(); $event.stopPropagation()">{{ t.CLEAR_ALL }}</button>
          </div>
          <div class="status-options">
            @for (language of languageOptions; track language.value) {
              <label class="status-option">
                <div class="custom-checkbox">
                  <input
                    type="checkbox"
                    [checked]="selectedLanguages.includes(language.value)"
                    (change)="toggleLanguage(language.value); $event.stopPropagation()"
                  />
                  <span class="checkmark"></span>
                </div>
                <span>{{ language.label }}</span>
              </label>
            }
          </div>
        </div>
      </div>
    </div>

    <div class="field-group">
      <label>{{ t.KEYWORDS }}</label>
      <div class="keyword-select-container">
        <div class="status-select-trigger" (click)="toggleKeywordDropdown(); $event.stopPropagation()">
          <span *ngIf="selectedKeywords.length === 0" class="placeholder">{{ t.FILTER_BY_KEYWORDS }}</span>
          <span *ngIf="selectedKeywords.length > 0" class="selected-count">{{ selectedCountLabel(selectedKeywords.length) }}</span>
          <i class="bi bi-chevron-down"></i>
        </div>
        <div class="status-dropdown" [class.show]="isKeywordDropdownOpen">
          <div class="dropdown-header">
            <span>{{ t.SELECT_LABEL }} {{ t.KEYWORDS }}</span>
            <button type="button" class="clear-btn" (click)="clearKeywords(); $event.stopPropagation()">{{ t.CLEAR_ALL }}</button>
          </div>
          <div class="status-options">
            @for (keyword of keywordOptions; track keyword.value) {
              <label class="status-option">
                <div class="custom-checkbox">
                  <input
                    type="checkbox"
                    [checked]="selectedKeywords.includes(keyword.value)"
                    (change)="toggleKeyword(keyword.value); $event.stopPropagation()"
                  />
                  <span class="checkmark"></span>
                </div>
                <span>{{ keyword.label }}</span>
              </label>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- AI Match Score - mirrors the same three tiers the Profile Match
         badge itself uses (match-low/medium/high), so the filter and the
         indicator the user is scanning for describe the same thresholds. -->
    <div class="field-group">
      <label>{{ t.MATCH_SCORE }}</label>
      <div class="segmented-control" role="group" [attr.aria-label]="t.MATCH_SCORE">
        <button
          type="button"
          class="segmented-btn"
          [class.is-active]="minMatchScore === null"
          (click)="setMinMatchScore(null)"
        >{{ t.MATCH_SCORE_ANY }}</button>
        @for (preset of matchScorePresets; track preset) {
          <button
            type="button"
            class="segmented-btn"
            [class.is-active]="minMatchScore === preset"
            (click)="setMinMatchScore(preset)"
          >{{ preset }}%+</button>
        }
      </div>
      <p class="field-hint">{{ t.MATCH_SCORE_HINT }}</p>
    </div>

    <!-- Document availability - Any / Available / Missing per document,
         a 3-button segmented control rather than a checkbox: a checkbox
         only has 2 native states, so it couldn't represent "explicitly
         excluded" (Missing) as visually distinct from "don't care" (Any),
         and there was no way back to "Any" once toggled away from it. -->
    <div class="field-group">
      <label>{{ t.DOCUMENT_AVAILABILITY }}</label>
      <div class="document-filters">
        <div class="document-filter-row">
          <span class="document-filter-label">{{ t.CV_AVAILABLE }}</span>
          <div class="segmented-control">
            <button type="button" class="segmented-btn" [class.is-active]="hasCV === null" (click)="setHasCV(null)">{{ t.DOCUMENTS_ALL }}</button>
            <button type="button" class="segmented-btn" [class.is-active]="hasCV === true" (click)="setHasCV(true)">{{ t.DOCUMENTS_AVAILABLE }}</button>
            <button type="button" class="segmented-btn" [class.is-active]="hasCV === false" (click)="setHasCV(false)">{{ t.DOCUMENTS_MISSING }}</button>
          </div>
        </div>
        <div class="document-filter-row">
          <span class="document-filter-label">{{ t.COVER_LETTER_AVAILABLE }}</span>
          <div class="segmented-control">
            <button type="button" class="segmented-btn" [class.is-active]="hasCoverLetter === null" (click)="setHasCoverLetter(null)">{{ t.DOCUMENTS_ALL }}</button>
            <button type="button" class="segmented-btn" [class.is-active]="hasCoverLetter === true" (click)="setHasCoverLetter(true)">{{ t.DOCUMENTS_AVAILABLE }}</button>
            <button type="button" class="segmented-btn" [class.is-active]="hasCoverLetter === false" (click)="setHasCoverLetter(false)">{{ t.DOCUMENTS_MISSING }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="bottom-section">
    <button type="button" class="apply-btn" (click)="applyFilters()">
      {{ t.APPLY_FILTERS }}
    </button>
    <button type="button" class="clear-all-btn" (click)="clearAllFilters()">
      {{ t.CLEAR_ALL }}
    </button>
  </div>
</div>
